import { Injectable } from '@angular/core';
import { OdataOperation, OdataService } from '@mono-repo/odata';
import { environment } from '../../../environments_mc';
import { ILogs, SystemEnvironment } from '@mono-repo/data';
import { of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  constructor(private readonly odataService: OdataService) {}
  /**
   * Generates a log entry with a random token, platform information, and login timestamp.
   * This method is used for logging purposes to keep track of user activity and platform usage.
   * The session token is necessary to uniquely identify the user's session and is currently set up
   * for future use in tracking logout events.
   * @returns {Observable<ILogs>} An observable that emits the result of the log posting operation.
   */
  public sendUserTrackingLogs() {
    const log = {
      token: sessionStorage.getItem('tokenForLoggingOfUserSessions'),
      plattform: this.getPlatformDevice(),
      loginTimestamp: sessionStorage.getItem('loginTime'),
    };
    return of({}); // deactivated because of oAuth complications
    // in future the logging is moved to cloud by @Ruben
    return this.odataService.post<ILogs>(new OdataOperation().Query('Logs'), log);
  }

  /**
   * Generates a random token string.
   *
   * This function creates a string composed of random characters from a predefined set,
   * including uppercase and lowercase letters, digits, and specific symbols. It utilizes
   * Math.random() to generate random indices for selecting characters, thus it's not suitable
   * for generating security-critical tokens. However, it is sufficient for generating unique
   * identifiers, random keys, or similar use cases where high-level cryptographic security
   * is not required.
   *
   * Note: Due to the use of Math.random(), this method should not be used for generating
   * tokens that require cryptographic strength, such as passwords, security tokens, or similar.
   *
   * @param length - The length of the token to be generated. Defaults to 32 characters.
   *                 This parameter allows customization of the token's length according
   *                 to the needs of the application.
   *
   * @returns A string representing the generated random token. The length of the string
   *          will match the specified length parameter. The characters used in the token
   *          are from the set [A-Za-z0-9-_].
   *
   * Usage example:
   * const token = generateRandomToken();
   * console.log(token); // Outputs a 32-character random token.
   * const shortToken = generateRandomToken(16);
   * console.log(shortToken); // Outputs a 16-character random token.
   */

  public generateRandomToken(): string {
    // Define the characters to use in the token.
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_#*!§$%&';

    let token = '';
    for (let i = 0; i < 128; i++) {
      // Generate a random index to pick a character from the characters string.
      const randomIndex = Math.floor(Math.random() * characters.length);
      token += characters.charAt(randomIndex);
    }
    return token;
  }

  /**
   * Determines the platform device based on the user agent or the Cordova platform ID.
   *
   * @private
   * @returns {string} The platform device. Possible values are "Windows", "Mac", "Linux", or the Cordova platform ID.
   */
  private getPlatformDevice() {
    if (environment.systemEnvironment != SystemEnvironment.APP) {
      const userAgent = navigator.userAgent;
      if (userAgent.indexOf('Windows') !== -1) {
        return 'Windows';
      } else if (userAgent.indexOf('Mac') !== -1) {
        return 'Mac';
      } else {
        return 'Linux';
      }
    } else {
      return window['cordova'].platformId;
    }
  }
}
